import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { format, parseISO, isSameDay } from 'date-fns';
import { 
  CalendarDays, 
  Clock, 
  PlusCircle, 
  Edit, 
  Trash2, 
  Droplets, 
  TrendingUp, 
  Heart,
  Sparkles,
  Moon,
  Sun,
  Flower,
  Star,
  Brain,
  Activity,
  BarChart3,
  Target,
  Lightbulb,
  AlertCircle,
  CheckCircle,
  ArrowUp,
  ArrowDown,
  Minus,
  Shield,
  Database
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCycle } from '../context/CycleContext';
import { useInsights } from '../context/InsightsContext';
import CycleLogForm from '../components/cycle/CycleLogForm';
import DataQualityIndicator from '../components/insights/DataQualityIndicator';

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { logs, stats, loading, deleteLog } = useCycle();
  const { generatedInsights, cycleStats } = useInsights();
  const [showLogForm, setShowLogForm] = useState(false);
  const [editingLog, setEditingLog] = useState<number | null>(null);
  
  const handleAddLog = () => {
    setEditingLog(null);
    setShowLogForm(true);
  };
  
  const handleEditLog = (id: number) => {
    setEditingLog(id);
    setShowLogForm(true);
  };
  
  const handleDeleteLog = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this log?')) {
      await deleteLog(id);
    }
  };

  const handleCloseForm = () => {
    setShowLogForm(false);
    setEditingLog(null);
  };

  const recentLogs = [...logs].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  ).slice(0, 5);

  const isToday = (dateString: string) => {
    return isSameDay(parseISO(dateString), new Date());
  };

  const getPhaseTranslation = (phase: string) => {
    return t(`dashboard.${phase}`);
  };

  const getPhaseColor = (phase: string) => {
    switch (phase) {
      case 'menstrual': return 'from-rose-400 via-pink-500 to-red-400';
      case 'follicular': return 'from-emerald-400 via-teal-500 to-cyan-400';
      case 'ovulation': return 'from-amber-400 via-orange-500 to-yellow-400';
      case 'luteal': return 'from-purple-400 via-violet-500 to-indigo-400';
      default: return 'from-pink-400 via-rose-500 to-purple-400';
    }
  };

  const getPhaseIcon = (phase: string) => {
    switch (phase) {
      case 'menstrual': return Droplets;
      case 'follicular': return Flower;
      case 'ovulation': return Sun;
      case 'luteal': return Moon;
      default: return Heart;
    }
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'cycle_prediction': return TrendingUp;
      case 'pattern_analysis': return BarChart3;
      case 'symptom_correlation': return Target;
      case 'health_tip': return Lightbulb;
      case 'data_quality': return Database;
      default: return Brain;
    }
  };

  const getInsightColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'from-rose-500 to-pink-500';
      case 'medium': return 'from-amber-500 to-orange-500';
      case 'low': return 'from-emerald-500 to-teal-500';
      default: return 'from-purple-500 to-indigo-500';
    }
  };

  const getTrendIcon = (trend?: string) => {
    switch (trend) {
      case 'improving': return ArrowUp;
      case 'concerning': return ArrowDown;
      case 'stable': return Minus;
      default: return Activity;
    }
  };

  const getTrendColor = (trend?: string) => {
    switch (trend) {
      case 'improving': return 'text-emerald-600';
      case 'concerning': return 'text-rose-600';
      case 'stable': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  // Get data quality insight
  const dataQualityInsight = generatedInsights.find(insight => insight.type === 'data_quality');
  const hasDataQualityIssues = dataQualityInsight && (dataQualityInsight.priority === 'high' || dataQualityInsight.priority === 'medium');

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <motion.div
          className="relative"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <motion.div
            className="w-12 h-12 border-4 border-rose-200 rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <motion.div
            className="absolute top-0 left-0 w-12 h-12 border-4 border-transparent border-t-rose-500 rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="space-y-6 sm:space-y-8"
    >
      {/* Data Quality Alert Banner */}
      {hasDataQualityIssues && (
        <motion.div
          className="bg-gradient-to-r from-amber-50 via-orange-50 to-red-50 border-l-4 border-l-amber-500 rounded-2xl p-4 mb-6"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-start space-x-3">
            <motion.div
              className="p-2 bg-amber-500 rounded-lg"
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <AlertCircle className="w-5 h-5 text-white" />
            </motion.div>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900 mb-1">Data Quality Notice</h3>
              <p className="text-amber-800 text-sm mb-2">
                We've detected some potential accuracy issues in your cycle data. This may affect the reliability of our insights and predictions.
              </p>
              <div className="flex flex-wrap gap-2">
                {dataQualityInsight?.validationFlags?.slice(0, 3).map((flag, index) => (
                  <span key={index} className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
                    {flag.replace('_', ' ')}
                  </span>
                ))}
              </div>
            </div>
            <Shield className="w-5 h-5 text-amber-600" />
          </div>
        </motion.div>
      )}

      {/* Welcome Header with Feminine Design */}
      <motion.div
        className="relative overflow-hidden bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 rounded-3xl p-6 sm:p-8 text-white"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute top-4 right-4 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
        <div className="absolute bottom-4 left-4 w-24 h-24 bg-pink-300/20 rounded-full blur-xl"></div>
        
        <div className="relative z-10">
          <motion.div
            className="flex items-center space-x-2 mb-4"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Sparkles className="w-6 h-6 text-yellow-300" />
            <span className="text-pink-100">Welcome back, beautiful!</span>
            {cycleStats && cycleStats.dataQuality >= 80 && (
              <motion.span 
                className="px-2 py-1 bg-white/20 text-white text-xs rounded-full"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                ✨ High Quality Data
              </motion.span>
            )}
          </motion.div>
          
          <motion.h1
            className="text-2xl sm:text-3xl font-bold mb-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Your Wellness Journey ✨
          </motion.h1>
          
          <motion.p
            className="text-pink-100 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Track your cycle, understand your body, and embrace your natural rhythm
          </motion.p>

          <motion.button
            onClick={handleAddLog}
            className="flex items-center space-x-2 px-6 py-3 bg-white/20 backdrop-blur-sm text-white rounded-2xl font-medium hover:bg-white/30 transition-all duration-300 shadow-lg"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <PlusCircle size={18} />
            <span>{t('dashboard.log_today')}</span>
          </motion.button>
        </div>
      </motion.div>

      {/* Stats Cards with Data Quality Indicator */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <motion.div 
          className="bg-gradient-to-br from-white to-rose-50 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-lg border border-rose-100 hover:shadow-xl transition-all duration-300"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          whileHover={{ y: -5, scale: 1.02 }}
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-rose-600 mb-1">{t('dashboard.next_period')}</p>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                {stats.nextPeriodPrediction 
                  ? (() => {
                      const days = Math.ceil((new Date(stats.nextPeriodPrediction).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                      if (days === 0) return t('dashboard.today');
                      if (days === 1) return t('dashboard.tomorrow');
                      if (days > 0) return t('dashboard.in_days', { days });
                      return t('dashboard.insufficient_data');
                    })()
                  : t('dashboard.insufficient_data')}
              </h3>
              {cycleStats && cycleStats.dataQuality < 70 && (
                <p className="text-xs text-amber-600">⚠️ Limited by data quality</p>
              )}
            </div>
            <motion.div
              className="p-3 bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl shadow-lg"
              whileHover={{ rotate: 10, scale: 1.1 }}
            >
              <CalendarDays className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </motion.div>
          </div>
        </motion.div>
        
        <motion.div 
          className="bg-gradient-to-br from-white to-purple-50 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-lg border border-purple-100 hover:shadow-xl transition-all duration-300"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          whileHover={{ y: -5, scale: 1.02 }}
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-purple-600 mb-1">{t('dashboard.cycle_phase')}</p>
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1">
                {getPhaseTranslation(stats.currentPhase)}
              </h3>
              <p className="text-xs sm:text-sm text-gray-600">
                {t('dashboard.phase_day', { day: stats.phaseDay })}
              </p>
            </div>
            <motion.div
              className={`p-3 bg-gradient-to-br ${getPhaseColor(stats.currentPhase)} rounded-xl shadow-lg`}
              whileHover={{ rotate: 10, scale: 1.1 }}
            >
              {React.createElement(getPhaseIcon(stats.currentPhase), { 
                className: "h-5 w-5 sm:h-6 sm:w-6 text-white" 
              })}
            </motion.div>
          </div>
        </motion.div>
        
        <motion.div 
          className="bg-gradient-to-br from-white to-emerald-50 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-lg border border-emerald-100 hover:shadow-xl transition-all duration-300"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          whileHover={{ y: -5, scale: 1.02 }}
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-emerald-600 mb-1">{t('dashboard.cycle_stats')}</p>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
                {t('dashboard.duration_days', { days: stats.averageCycleLength })}
              </h3>
              <p className="text-xs sm:text-sm text-gray-600">Average cycle</p>
            </div>
            <motion.div
              className="p-3 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl shadow-lg"
              whileHover={{ rotate: 10, scale: 1.1 }}
            >
              <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </motion.div>
          </div>
        </motion.div>

        {/* Data Quality Card */}
        <motion.div 
          className="bg-gradient-to-br from-white to-blue-50 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-lg border border-blue-100 hover:shadow-xl transition-all duration-300"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          whileHover={{ y: -5, scale: 1.02 }}
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-blue-600 mb-1">Data Quality</p>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
                {cycleStats ? `${cycleStats.dataQuality}%` : 'N/A'}
              </h3>
              <p className="text-xs sm:text-sm text-gray-600">
                {cycleStats && cycleStats.dataQuality >= 80 ? 'Excellent' :
                 cycleStats && cycleStats.dataQuality >= 60 ? 'Good' :
                 cycleStats && cycleStats.dataQuality >= 40 ? 'Fair' : 'Needs improvement'}
              </p>
            </div>
            <motion.div
              className={`p-3 bg-gradient-to-br ${
                cycleStats && cycleStats.dataQuality >= 80 ? 'from-emerald-500 to-teal-500' :
                cycleStats && cycleStats.dataQuality >= 60 ? 'from-blue-500 to-indigo-500' :
                cycleStats && cycleStats.dataQuality >= 40 ? 'from-amber-500 to-orange-500' :
                'from-red-500 to-pink-500'
              } rounded-xl shadow-lg`}
              whileHover={{ rotate: 10, scale: 1.1 }}
            >
              <Database className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Data Quality Indicator */}
      {dataQualityInsight && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <DataQualityIndicator
            quality={cycleStats?.dataQuality || 0}
            flags={dataQualityInsight.validationFlags || []}
            severity={dataQualityInsight.priority === 'high' ? 'high' : dataQualityInsight.priority === 'medium' ? 'medium' : 'low'}
            className="mb-6"
          />
        </motion.div>
      )}

      {/* Health Insights Section with Enhanced Validation */}
      {generatedInsights && generatedInsights.length > 0 && (
        <motion.div
          className="bg-gradient-to-br from-white to-purple-50 backdrop-blur-sm rounded-2xl shadow-lg border border-purple-100 p-6 mb-6 sm:mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <motion.div
                className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl"
                whileHover={{ rotate: 10, scale: 1.1 }}
              >
                <Brain className="w-6 h-6 text-white" />
              </motion.div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Your Health Insights</h2>
                <p className="text-sm text-gray-600">
                  AI-powered analysis with data validation
                  {cycleStats && (
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                      cycleStats.dataQuality >= 80 ? 'bg-emerald-100 text-emerald-700' :
                      cycleStats.dataQuality >= 60 ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {cycleStats.dataQuality}% confidence
                    </span>
                  )}
                </p>
              </div>
            </div>
            <motion.div
              className="flex items-center space-x-2 px-3 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Sparkles className="w-4 h-4" />
              <span>{generatedInsights.length} insights</span>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {generatedInsights.slice(0, 4).map((insight, index) => {
              const IconComponent = getInsightIcon(insight.type);
              const TrendIconComponent = getTrendIcon(insight.trend);
              
              return (
                <motion.div
                  key={insight.id}
                  className={`bg-white/80 backdrop-blur-sm p-4 rounded-xl border transition-all duration-300 hover:shadow-md ${
                    insight.type === 'data_quality' && insight.priority === 'high' 
                      ? 'border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50' 
                      : 'border-gray-100'
                  }`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  whileHover={{ x: 4 }}
                >
                  <div className="flex items-start space-x-3">
                    <motion.div
                      className={`p-2 bg-gradient-to-br ${getInsightColor(insight.priority)} rounded-lg flex-shrink-0`}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      <IconComponent className="w-4 h-4 text-white" />
                    </motion.div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900 text-sm">{insight.title}</h4>
                        <div className="flex items-center space-x-1">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            insight.priority === 'high' ? 'bg-rose-100 text-rose-700' :
                            insight.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-emerald-100 text-emerald-700'
                          }`}>
                            {insight.priority}
                          </span>
                          {insight.trend && (
                            <div className={`flex items-center space-x-1 ${getTrendColor(insight.trend)}`}>
                              <TrendIconComponent className="w-3 h-3" />
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <p className="text-gray-700 text-sm mb-2 leading-relaxed">{insight.description}</p>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-purple-600 font-medium flex items-center">
                          <Shield className="w-3 h-3 mr-1" />
                          {insight.confidence}% confidence
                        </span>
                        {insight.actionable && (
                          <span className="text-xs text-gray-500">Actionable</span>
                        )}
                      </div>

                      {/* Data Quality Warning */}
                      {insight.dataQuality && insight.dataQuality !== 'excellent' && (
                        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-xs text-amber-700">
                            ⚠️ Analysis limited by {insight.dataQuality} data quality
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {generatedInsights.length > 4 && (
            <motion.div 
              className="text-center mt-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.0 }}
            >
              <button className="text-purple-600 hover:text-purple-700 font-medium text-sm">
                View all {generatedInsights.length} insights →
              </button>
            </motion.div>
          )}
        </motion.div>
      )}

      {/* Recent Logs Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0">
        <motion.h2 
          className="text-lg sm:text-xl font-semibold text-gray-900"
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          {t('dashboard.recent_logs')}
        </motion.h2>
      </div>

      <motion.div 
        className="bg-gradient-to-br from-white to-pink-50 backdrop-blur-sm rounded-2xl shadow-lg border border-pink-100 overflow-hidden mb-6 sm:mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
      >
        {recentLogs.length > 0 ? (
          <div className="divide-y divide-pink-100">
            {recentLogs.map((log, index) => (
              <motion.div 
                key={log.id} 
                className={`p-4 sm:p-6 hover:bg-pink-50/50 transition-all duration-300 ${log.period_start ? 'border-l-4 border-l-rose-500 bg-rose-50/30' : ''}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.0 + index * 0.1 }}
                whileHover={{ x: 4 }}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center mb-2 space-y-1 sm:space-y-0">
                      <span className={`text-sm font-medium ${isToday(log.date) ? 'text-rose-600' : 'text-gray-900'}`}>
                        {format(parseISO(log.date), 'MMMM d, yyyy')}
                        {isToday(log.date) && (
                          <motion.span 
                            className="ml-2 px-2 py-1 bg-rose-100 text-rose-800 text-xs rounded-full"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 1.2 + index * 0.1 }}
                          >
                            {t('dashboard.today')}
                          </motion.span>
                        )}
                      </span>
                      {log.period_start && (
                        <motion.span 
                          className="sm:ml-2 px-2 py-1 bg-rose-100 text-rose-800 text-xs rounded-full flex items-center space-x-1"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 1.2 + index * 0.1 }}
                        >
                          <Droplets className="w-3 h-3" />
                          <span>Period Day</span>
                        </motion.span>
                      )}
                    </div>
                    
                    <div className="space-y-1">
                      {log.flow_level && (
                        <p className="text-sm text-gray-600">
                          <span className="font-medium text-rose-700">Flow:</span> {t(`cycle_log.flow_${log.flow_level}`)}
                        </p>
                      )}
                      
                      {log.mood && (
                        <p className="text-sm text-gray-600">
                          <span className="font-medium text-purple-700">Mood:</span> {t(`moods.${log.mood}`)}
                        </p>
                      )}
                      
                      {log.symptoms && log.symptoms.length > 0 && (
                        <p className="text-sm text-gray-600">
                          <span className="font-medium text-emerald-700">Symptoms:</span> {log.symptoms.map(s => t(`symptoms.${s}`)).join(', ')}
                        </p>
                      )}
                      
                      {log.notes && (
                        <p className="text-sm text-gray-600 mt-2 italic bg-gray-50 p-2 rounded-lg">
                          "{log.notes}"
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 ml-4">
                    <motion.button
                      onClick={() => handleEditLog(log.id)}
                      className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all duration-300 min-h-[44px] min-w-[44px] flex items-center justify-center"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Edit size={16} />
                    </motion.button>
                    <motion.button
                      onClick={() => handleDeleteLog(log.id)}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-300 min-h-[44px] min-w-[44px] flex items-center justify-center"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Trash2 size={16} />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <motion.div 
            className="p-8 sm:p-12 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.0 }}
          >
            <motion.div
              className="w-16 h-16 bg-gradient-to-br from-rose-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4"
              whileHover={{ scale: 1.1, rotate: 10 }}
            >
              <Flower className="w-8 h-8 text-rose-500" />
            </motion.div>
            <p className="text-gray-500 mb-4 text-sm sm:text-base">
              {t('dashboard.no_logs')}
            </p>
            <motion.button
              onClick={handleAddLog}
              className="btn-mobile bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('dashboard.start_tracking')}
            </motion.button>
          </motion.div>
        )}
      </motion.div>

      {/* Log Form Modal */}
      <AnimatePresence>
        {showLogForm && (
          <motion.div
            className="modal-mobile bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseForm}
          >
            <motion.div
              className="modal-content-mobile bg-white/95 backdrop-blur-md shadow-2xl border border-white/20"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
                  {editingLog ? 'Edit Log' : 'Add New Log'}
                </h2>
                <CycleLogForm
                  logId={editingLog}
                  onClose={handleCloseForm}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Dashboard;